import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Users, Star, Gift, Plus, Trash2, RefreshCw, UserPlus, Search } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientsTab = ({ customers, onRefresh }) => {
  const [showNew, setShowNew] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', phone: '', birthday: '' });
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const filtered = customers.filter(c =>
    !search ||
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const createClient = async () => {
    if (!newClient.name || !newClient.email) return;
    setCreating(true);
    try {
      await axios.post(`${API}/admin/customers`, newClient);
      setNewClient({ name: '', email: '', phone: '', birthday: '' });
      setShowNew(false);
      onRefresh?.();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    } finally {
      setCreating(false);
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      await axios.delete(`${API}/admin/customers/${id}`);
      onRefresh?.();
    } catch (e) { alert('Erreur'); }
  };

  const resetAll = async () => {
    if (!window.confirm('ATTENTION : Supprimer TOUS les clients ? Cette action est irreversible.')) return;
    if (!window.confirm('Etes-vous vraiment sur ? Tous les comptes clients seront effaces.')) return;
    try {
      const res = await axios.delete(`${API}/admin/customers/reset`);
      alert(`${res.data.deleted_count} client(s) supprime(s)`);
      onRefresh?.();
    } catch (e) { alert('Erreur'); }
  };

  return (
    <>
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] mb-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold text-[#2D241D]" data-testid="clients-title">
              Clients inscrits ({customers.length})
            </h3>
            <p className="text-xs text-[#6E5E52]">
              Programme fidelite - 10 commandes = 1 plat offert |
              <Star className="w-3 h-3 inline mx-1 text-[#D4A853]" />
              {customers.filter(c => (c.order_count || 0) >= 10).length} recompenses
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={resetAll} variant="outline"
              className="rounded-xl border-red-300 text-red-500 hover:bg-red-50 text-xs h-9"
              data-testid="reset-clients-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
            <Button onClick={() => setShowNew(true)}
              className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl text-xs h-9"
              data-testid="add-client-btn">
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Nouveau client
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E5E52]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un client..." className="pl-10 rounded-xl border-[#E5DCD3]"
            data-testid="client-search" />
        </div>
      </div>

      {/* Create Client Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md bg-white rounded-3xl admin-theme">
          <DialogHeader><DialogTitle className="text-[#2D241D]">Nouveau client</DialogTitle></DialogHeader>
          <div className="space-y-4 p-4">
            <div>
              <label className="text-sm font-medium text-[#2D241D] mb-1 block">Nom *</label>
              <Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                placeholder="Jean Dupont" className="rounded-xl border-[#E5DCD3]" data-testid="new-client-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D241D] mb-1 block">Email *</label>
              <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                placeholder="jean@example.com" className="rounded-xl border-[#E5DCD3]" data-testid="new-client-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">Telephone</label>
                <Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="+66..." className="rounded-xl border-[#E5DCD3]" data-testid="new-client-phone" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">Anniversaire</label>
                <Input type="date" value={newClient.birthday} onChange={(e) => setNewClient({ ...newClient, birthday: e.target.value })}
                  className="rounded-xl border-[#E5DCD3]" data-testid="new-client-birthday" />
              </div>
            </div>
            <Button onClick={createClient} disabled={!newClient.name || !newClient.email || creating}
              className="w-full bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl" data-testid="save-new-client-btn">
              <UserPlus className="w-4 h-4 mr-2" /> {creating ? 'Creation...' : 'Creer le client'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile cards */}
      <div className="space-y-3 sm:hidden">
        {filtered.map(c => {
          const pct = Math.min(((c.order_count || 0) % 10) / 10 * 100, 100);
          const hasReward = (c.order_count || 0) >= 10 && ((c.order_count || 0) / 10 > (c.rewards_claimed || 0));
          return (
            <div key={c.id} className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid={`customer-card-${c.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-[#2D241D]">{c.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-[#E16833]">{c.order_count || 0} cmd</span>
                  <button onClick={() => deleteClient(c.id)}
                    className="text-red-400 hover:text-red-600 p-1" data-testid={`delete-client-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-[#6E5E52] break-all">{c.email}</p>
              {c.phone && <p className="text-xs text-[#6E5E52] mt-0.5">{c.phone}</p>}
              {c.birthday && <p className="text-xs text-[#6E5E52] mt-0.5">Anniversaire: {c.birthday}</p>}
              <div className="flex items-center gap-2 mt-2">
                <div className="flex-1 bg-[#E5DCD3] rounded-full h-2">
                  <div className="bg-gradient-to-r from-[#D4A853] to-[#E16833] h-2 rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-[#6E5E52]">{(c.order_count || 0) % 10}/10</span>
                {hasReward && <Gift className="w-4 h-4 text-[#E16833]" />}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-[#6E5E52] bg-white rounded-2xl border border-[#E5DCD3]">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Aucun client</p>
          </div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F3EDE5]">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#2D241D]">Client</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#2D241D]">Email</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#2D241D]">Telephone</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#2D241D]">Anniversaire</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#2D241D]">Commandes</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#2D241D]">Fidelite</th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const pct = Math.min(((c.order_count || 0) % 10) / 10 * 100, 100);
              const hasReward = (c.order_count || 0) >= 10 && ((c.order_count || 0) / 10 > (c.rewards_claimed || 0));
              return (
                <tr key={c.id} className="border-t border-[#E5DCD3] hover:bg-[#F3EDE5]/50" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-[#2D241D]">{c.name}</td>
                  <td className="px-4 py-3 text-sm text-[#6E5E52]">{c.email}</td>
                  <td className="px-4 py-3 text-sm text-[#6E5E52]">{c.phone || '-'}</td>
                  <td className="px-4 py-3 text-sm text-[#6E5E52]">{c.birthday || '-'}</td>
                  <td className="px-4 py-3 font-bold text-[#E16833]">{c.order_count || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-[#E5DCD3] rounded-full h-2">
                        <div className="bg-gradient-to-r from-[#D4A853] to-[#E16833] h-2 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      {hasReward && <Gift className="w-4 h-4 text-[#E16833]" />}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => deleteClient(c.id)}
                      className="text-red-400 hover:text-red-600 p-1" data-testid={`delete-client-row-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-[#6E5E52]">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Aucun client</p>
          </div>
        )}
      </div>
    </>
  );
};
